import { ReactNode } from "react"
import { useAsync } from "react-use"
import BlocksConfigProvider, {
  BlocksConfigContextType
} from "../config/BlocksConfigProvider"
import { useEditorStateSelector } from "../editor/EditorStateProvider"
import GraphQLSyncer from "../syncer/GraphQLSyncer"
import { DefaultError } from "../syncer/DefaultError"
import { DefaultLoading } from "../syncer/DefaultLoading"
import { BlocksContentType } from "../types"
import { SyncerContextType, useSyncer } from "../syncer/SyncerContext"
import ViewerStateProvider, {
  ViewerContextValueType,
  ViewerStateProviderPropsType
} from "./ViewerStateProvider"

function DraftPreviewWithSyncer({
  loadPreviewContent,
  ...props
}: ViewerStateProviderPropsType & {
  loadPreviewContent: () => Promise<BlocksContentType>
}) {
  const { value, error, loading } = useAsync(loadPreviewContent, [
    loadPreviewContent
  ])
  if (error) return <DefaultError message={error.message} />
  if (loading || !value) return <DefaultLoading />
  return <ViewerStateProvider {...props} content={value} />
}

export const ViewerStateWithSyncer = ({
  children,
  draft
}: {
  children: ReactNode | ((props: ViewerContextValueType) => ReactNode)
  // This allows student data to be saved in Editor Preview mode
  draft?: boolean
}) => {
  const syncerState: SyncerContextType = useSyncer()
  const editorBlocks = useEditorStateSelector((state) => state.blocks)

  const { content } = syncerState

  const viewerProps = {
    ...(syncerState as Omit<ViewerStateProviderPropsType, "children">),
    content: {
      ...content!,
      blocks: editorBlocks?.length ? editorBlocks : content!.blocks
    },
    draft,
    children
  }

  if (draft && syncerState.loadPreviewContent)
    return (
      <DraftPreviewWithSyncer
        {...viewerProps}
        loadPreviewContent={syncerState.loadPreviewContent}
      />
    )

  return <ViewerStateProvider {...viewerProps} />
}

export type BlocksViewContainerProps = BlocksConfigContextType & {
  children: ReactNode | ((props: ViewerContextValueType) => ReactNode)
  Syncer?: any
  renderLoading?: (message?: string) => JSX.Element
}

export const BlocksViewContainer = ({
  children,
  Syncer,
  renderLoading,
  ...config
}: BlocksViewContainerProps) => {
  const SyncProvider = Syncer || GraphQLSyncer

  const syncerProps: Record<string, any> = {}

  if (!Syncer) {
    syncerProps.graphQLClient = config.graphQLClient
    syncerProps.authToken = config.authToken
    syncerProps.graphQLEndpoint = config.graphQLEndpoint
    syncerProps.accountID = config.accountID
  }

  return (
    <BlocksConfigProvider {...config} renderLoading={renderLoading}>
      <SyncProvider {...syncerProps}>
        <ViewerStateWithSyncer>{children}</ViewerStateWithSyncer>
      </SyncProvider>
    </BlocksConfigProvider>
  )
}

export default BlocksViewContainer
